import { Close as CloseIcon, Phone as PhoneIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useGetUsersCommunicationsQuery } from "../../../store/services/communicationService";
import {
  CommunicationTypeEnum,
  type CustomerCommunication,
} from "../../../store/types/communication.types";

interface CustomerCallButtonProps {
  customerId?: number;
}

const isPhoneType = (type?: CommunicationTypeEnum): boolean =>
  type === CommunicationTypeEnum.Telefon ||
  type === CommunicationTypeEnum.Mobiltelefon;

const formatTelHref = (value: string): string =>
  `tel:${value.replace(/[^\d+]/g, "")}`;

const phoneTypeLabel = (type?: CommunicationTypeEnum): string =>
  type === CommunicationTypeEnum.Mobiltelefon ? "Mobil" : "Telefon";

const contactName = (comm: CustomerCommunication): string => {
  const cp = comm.ContactPerson;
  if (!cp) return "";
  return `${cp.Firstname ?? ""} ${cp.Lastname ?? ""}`.trim();
};

const fabPlacementSx = {
  position: "absolute" as const,
  bottom: 120,
  right: 16,
  display: { xs: "flex", sm: "none" },
  zIndex: 2,
};

const CustomerCallButton = ({ customerId }: CustomerCallButtonProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data = [] } = useGetUsersCommunicationsQuery(
    customerId ?? skipToken
  );

  if (!customerId) return null;

  const phones = data.filter(
    (c): c is CustomerCommunication & { Value: string } =>
      isPhoneType(c.Type) && Boolean(c.Value)
  );

  if (phones.length === 0) return null;

  const sortedPhones = [...phones].sort((a, b) => {
    if (a.IsPrimary !== b.IsPrimary) return a.IsPrimary ? -1 : 1;
    if (a.Type !== b.Type)
      return a.Type === CommunicationTypeEnum.Mobiltelefon ? -1 : 1;
    return contactName(a).localeCompare(contactName(b));
  });

  if (sortedPhones.length === 1) {
    const phone = sortedPhones[0];
    const name = contactName(phone);
    const tooltipTitle = name
      ? `Ring ${name} (${phoneTypeLabel(phone.Type)})`
      : `Ring ${phoneTypeLabel(phone.Type)}`;
    return (
      <Box sx={fabPlacementSx}>
        <Tooltip title={tooltipTitle}>
          <Fab
            color="primary"
            component="a"
            href={formatTelHref(phone.Value)}
            aria-label="Ring kund"
          >
            <PhoneIcon />
          </Fab>
        </Tooltip>
      </Box>
    );
  }

  return (
    <>
      <Box sx={fabPlacementSx}>
        <Tooltip title="Ring kund">
          <Fab
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
            aria-label="Ring kund"
          >
            <PhoneIcon />
          </Fab>
        </Tooltip>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Ring kund
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="small"
            aria-label="Stäng"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {sortedPhones.map((phone) => {
              const name = contactName(phone);
              return (
                <ListItem
                  key={`${phone.CommunicationNo}-${phone.Value}`}
                  disablePadding
                >
                  <ListItemButton
                    component="a"
                    href={formatTelHref(phone.Value)}
                    onClick={() => setDialogOpen(false)}
                  >
                    <ListItemIcon>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={phone.IsPrimary ? 600 : 400}
                          >
                            {name || "Ingen kontakt"}
                          </Typography>
                          {phone.IsPrimary && (
                            <Chip
                              label="Primär"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={`${phoneTypeLabel(phone.Type)} • ${phone.Value}`}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerCallButton;
